import { Box, Button, ConfirmDialog, Stack, Table, toast, Typography } from '@montugroup/design-system';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import { useMediaQuery, useTheme } from '@mui/material';
import type { CellContext, Row } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import type { StocktakeItem } from '@/hooks/stocktake/useStocktake';
import { useStocktake } from '@/hooks/stocktake/useStocktake';
import { STOCKTAKE_PRINT_ROUTE } from '@/routes/route-paths';
import { AddStocktakeProductsModal } from './AddStocktakeProductsModal';
import { StocktakeDiscrepancyBadge } from './StocktakeDiscrepancyBadge';
import { StocktakeDiscrepancyReason } from './StocktakeDiscrepancyReason';
import { StocktakeError } from './StocktakeError';
import { StocktakeItemCountInput } from './StocktakeItemCountInput';
import { StocktakeSkeleton } from './StocktakeSkeleton';
import { compareByName } from './stocktakeSort';
import { needsReviewRowSx, numericColumnMeta } from './stocktakeTableStyles';
import type { SubmitStocktakeItem } from './SubmitStocktakeModal';
import SubmitStocktakeModal from './SubmitStocktakeModal';

type StocktakeCellProps = CellContext<StocktakeItem, unknown>;

const buildSubmitStocktakeItems = (stocktakeItems: readonly StocktakeItem[]): SubmitStocktakeItem[] => {
  return stocktakeItems.map(
    (item): SubmitStocktakeItem => ({
      productId: item.productId,
      name: item.name,
      expectedStock: item.expectedStock,
      countedStock: item.countedStock ?? item.expectedStock,
      discrepancy: item.discrepancy ?? 0,
      reason: item.reason,
    }),
  );
};

const hasDiscrepancy = (discrepancy: number | undefined): boolean => {
  return discrepancy !== undefined && discrepancy !== 0;
};

const isStocktakeItemEdited = (item: StocktakeItem): boolean => {
  return item.countedStock !== undefined || item.reason !== undefined;
};

// The design-system Table's default export is memoised, which erases its row type
const applyRowStyles = (row: Row<unknown>) => {
  return hasDiscrepancy((row.original as StocktakeItem).discrepancy) ? needsReviewRowSx : {};
};

const INITIAL_SORT = [{ id: 'name', desc: false }];

// Fits the longest reason in the select, so the column doesn't widen when the first discrepancy shows it
const REASON_COLUMN_MIN_WIDTH = '14rem';

// Shares of the table's width, so columns keep their size as paging and counting change the rows
const COLUMN_WIDTHS = {
  name: '34%',
  sku: '20%',
  expectedStock: '8%',
  countedStock: '10%',
  discrepancy: '8%',
  reason: '20%',
} as const;

const reasonColumnMeta = { sx: { minWidth: REASON_COLUMN_MIN_WIDTH, width: COLUMN_WIDTHS.reason } } as const;

// Each count saves the whole stocktake to localStorage and re-renders the table, so wait for typing to pause
const COUNT_DEBOUNCE_MS = 250;

type ClearStocktakeDialogProps = Readonly<{
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}>;

const ClearStocktakeDialog = ({ open, onCancel, onConfirm }: ClearStocktakeDialogProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <ConfirmDialog
      open={open}
      onClose={onCancel}
      fullWidth
      fullScreen={isSmallScreen}
      maxWidth="xs"
      title="Clear stocktake?"
      actions={
        <>
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onConfirm}>
            Clear stocktake
          </Button>
        </>
      }
    >
      Any changes you&apos;ve made will be lost.
    </ConfirmDialog>
  );
};

export default function Stocktake() {
  const {
    status,
    stocktakeItems,
    addableProducts,
    retry,
    startNewStocktake,
    addStocktakeItems,
    setStocktakeItemCount,
    setStocktakeItemReason,
    clearStocktake,
  } = useStocktake();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Product Name',
        enableSorting: true,
        meta: { sx: { width: COLUMN_WIDTHS.name } },
        // The Table's default text sort compares character codes, which puts accented names after 'z'
        sortingFn: (a: Row<StocktakeItem>, b: Row<StocktakeItem>) => compareByName(a.original, b.original),
      },
      {
        accessorKey: 'sku',
        header: 'SKU',
        enableSorting: true,
        meta: {
          sx: { width: COLUMN_WIDTHS.sku },
          bodySx: { color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
        },
      },
      {
        accessorKey: 'expectedStock',
        header: 'Expected Stock',
        enableSorting: false,
        meta: { ...numericColumnMeta, sx: [numericColumnMeta.sx, { width: COLUMN_WIDTHS.expectedStock }] },
      },
      {
        accessorKey: 'countedStock',
        header: 'Counted Stock',
        enableSorting: false,
        meta: { ...numericColumnMeta, sx: [numericColumnMeta.sx, { width: COLUMN_WIDTHS.countedStock }] },
        cell: ({ row }: StocktakeCellProps) => (
          <StocktakeItemCountInput
            productId={row.original.productId}
            count={row.original.countedStock}
            debounceMs={COUNT_DEBOUNCE_MS}
            onCountChange={setStocktakeItemCount}
          />
        ),
      },
      {
        accessorKey: 'discrepancy',
        header: 'Discrepancy',
        enableSorting: false,
        meta: { ...numericColumnMeta, sx: [numericColumnMeta.sx, { width: COLUMN_WIDTHS.discrepancy }] },
        cell: ({ row }: StocktakeCellProps) => <StocktakeDiscrepancyBadge discrepancy={row.original.discrepancy} />,
      },
      {
        accessorKey: 'reason',
        header: 'Reason',
        enableSorting: false,
        meta: reasonColumnMeta,
        cell: ({ row }: StocktakeCellProps) => {
          if (!hasDiscrepancy(row.original.discrepancy)) {
            return null;
          }
          return (
            <StocktakeDiscrepancyReason
              productId={row.original.productId}
              reason={row.original.reason}
              onReasonChange={setStocktakeItemReason}
            />
          );
        },
      },
    ],
    [setStocktakeItemCount, setStocktakeItemReason],
  );

  const submitItems = useMemo(() => buildSubmitStocktakeItems(stocktakeItems), [stocktakeItems]);

  const discrepancyCount = stocktakeItems.filter((item) => hasDiscrepancy(item.discrepancy)).length;
  const isStocktakeInProgress = status === 'inProgress';
  const hasEditedItems = stocktakeItems.some(isStocktakeItemEdited);

  const onClearClick = () => {
    if (hasEditedItems) {
      setIsClearDialogOpen(true);
      return;
    }
    clearStocktake();
  };

  const onConfirmClear = () => {
    setIsClearDialogOpen(false);
    clearStocktake();
  };

  const onAddProducts = (productIds: readonly number[]) => {
    addStocktakeItems(productIds);
    setIsAddModalOpen(false);
    toast.success(`${productIds.length} ${productIds.length === 1 ? 'product' : 'products'} added`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" gap={3} paddingTop={5} paddingBottom={6}>
        <Typography variant="h5" component="h2">
          Stocktake
        </Typography>
        {isStocktakeInProgress && discrepancyCount > 0 && (
          <Typography variant="body2" sx={{ color: 'warning.dark' }}>
            {discrepancyCount} {discrepancyCount === 1 ? 'item' : 'items'} to review
          </Typography>
        )}
      </Stack>
      {status === 'loading' && <StocktakeSkeleton />}
      {status === 'error' && <StocktakeError onRetry={retry} />}
      {status === 'notStarted' && (
        <Box>
          <Button variant="contained" size="large" onClick={startNewStocktake}>
            Start new stocktake
          </Button>
        </Box>
      )}
      {isStocktakeInProgress && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={4} justifyContent="flex-end" paddingBottom={6}>
            <Stack direction="row" flexWrap="wrap" gap={4} sx={{ marginRight: { sm: 'auto' } }}>
              <Button
                component="a"
                href={STOCKTAKE_PRINT_ROUTE}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="large"
                startIcon={<PrintIcon />}
              >
                Printable version
              </Button>
              {addableProducts.length > 0 && (
                <Button variant="outlined" size="large" startIcon={<AddIcon />} onClick={() => setIsAddModalOpen(true)}>
                  Add products
                </Button>
              )}
            </Stack>
            <Box>
              <Button variant="outlined" size="large" onClick={onClearClick}>
                Clear
              </Button>
            </Box>
            <Box>
              <Button variant="contained" size="large" onClick={() => setIsSubmitModalOpen(true)}>
                Submit
              </Button>
            </Box>
          </Stack>
          <Table
            data={stocktakeItems}
            columns={columns}
            initialSort={INITIAL_SORT}
            showPagination
            autoResetPageIndex={false}
            hasRowBackgroundColor={false}
            compact
            applyRowStyles={applyRowStyles}
          />
          <AddStocktakeProductsModal
            open={isAddModalOpen}
            products={addableProducts}
            onClose={() => setIsAddModalOpen(false)}
            onAdd={onAddProducts}
          />
          <SubmitStocktakeModal
            open={isSubmitModalOpen}
            items={submitItems}
            onClose={() => setIsSubmitModalOpen(false)}
            onSubmitted={clearStocktake}
          />
        </>
      )}

      <ClearStocktakeDialog
        open={isClearDialogOpen}
        onCancel={() => setIsClearDialogOpen(false)}
        onConfirm={onConfirmClear}
      />
    </Box>
  );
}
